import React, {useEffect, Component} from 'react'
import ReactGA from 'react-ga4';
import { HelmetProvider } from 'react-helmet-async';
import AppRouter from './router';
import { useLocation } from 'react-router-dom';
import { trackError } from './services/analytics';
import './styles/css/main.css';
import { useAuthInit } from './hooks/useAuthInit';


// Initialize Google Analytics 4
// This MUST happen before any tracking calls
ReactGA.initialize('G-3BHG7R32SF');

// Page title mapping for better GA reports
const PAGE_TITLES = {
  '/': 'Homepage',
  '/about': 'About Us',
  '/contact': 'Contact',
  '/help': 'Help Center',
  '/courses': 'Courses',
  '/bundles': 'Bundles',
  '/sign-in': 'Sign In',
  '/sign-up': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/enrollment': 'Enrollment',
  '/welcome': 'Welcome',
  '/dashboard': 'Dashboard',
  '/dashboard/courses': 'My Courses',
  '/dashboard/bundles': 'My Bundles',
  '/dashboard/learning': 'Learning Progress',
  '/dashboard/settings': 'Settings',
  '/dashboard/billing': 'Billing',
  '/prompt-engineering-mastery': 'Prompt Engineering Mastery',
  '/ai-tools-accelerator': 'AI Tools Accelerator',
  '/ai-essentials-bundle': 'AI Essentials Bundle',
  '/privacy-policy': 'Privacy Policy',
  '/terms-of-service': 'Terms of Service',
  '/admin/dashboard': 'Admin Dashboard',
  '/admin/products': 'Admin - Products',
  '/admin/videos': 'Admin - Videos',
  '/admin/users': 'Admin - Users',
  '/admin/enrollments': 'Admin - Enrollments',
  '/admin/system': 'Admin - System',
  '/admin/error-logs': 'Admin - Error Logs',
};

// Get page title for a path
const getPageTitle = (pathname) => {
  // Exact match
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }

  // Handle dynamic routes
  if (pathname.startsWith('/classroom/course/')) {
    return 'Classroom';
  }
  if (pathname.startsWith('/enroll/')) {
    return 'Enrollment Checkout';
  }
  if (pathname.startsWith('/admin/error-logs/')) {
    return 'Admin - Error Details';
  }

  // Default to document title or pathname
  return document.title || pathname;
};


// ============================================================================
// Error Boundary with Analytics Tracking
// ============================================================================

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });

    // Track error to Google Analytics
    trackError.exception(
      error?.message || 'Unknown error',
      true, // fatal
      {
        component_stack: errorInfo?.componentStack || 'unknown',
        error_name: error?.name || 'Error',
        page_url: window.location.href,
      }
    );

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
            <svg
              className="w-16 h-16 text-red-500 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h1 className="text-xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-400 mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button
              onClick={this.handleReload}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}



function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}




function App() {

	// Initialize auth state from localStorage
	useAuthInit();

	let location = useLocation();

	// Track page views on route changes with proper page titles
	useEffect(() => {
		const pageTitle = getPageTitle(location.pathname);
		const fullPath = location.pathname + location.search;

		// Send page view to GA4 with title
		ReactGA.send({
			hitType: 'pageview',
			page: fullPath,
			title: pageTitle
		});

		// Also update document title for consistency
		if (PAGE_TITLES[location.pathname]) {
			document.title = `${pageTitle} | AI Mastery Hub`;
		}
	}, [location]);

	return (
		<HelmetProvider>
			<ErrorBoundary>
				<div>
					<ScrollToTop/>
					<AppRouter  />
				</div>
			</ErrorBoundary>
		</HelmetProvider>
	);
}

export default App;